import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { Plan } from "@ecologi/react-components/src/Plan";
import { planTypes } from "@ecologi/react-components/src/Plan/components/PlanSelectButtons";
import { useStore } from "../../../store";
import { useCreateSubscription, useUpdateSubscription } from "../hooks";
import { selectErrorMessageFromResult } from "../selectors";
import { Head } from "../../../components";
import { hasQueryInitiated } from "@ecologi/shared";
import { onSubmitCheckoutForm } from "@ecologi/react-components/src/CheckoutForm/utils/on-submit-checkout-form";
import { isProduction } from "../../../lib/utils/is-production";

export function ConsumerPlansPage() {
  const isProductionEnv = isProduction();

  const { user, location, stripe } = useStore();
  const { isReady: isRouterReady, query } = useRouter();

  const [selectedPlanType, setSelectedPlanType] = useState(planTypes.personal);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  const [createSubscriptionMutation, createSubscriptionResult] =
    useCreateSubscription();

  const [updateSubscriptionMutation, updateSubscriptionResult] =
    useUpdateSubscription();

  const hasCreationInitiated = hasQueryInitiated(createSubscriptionResult);

  // Passing 'isLoading' as true even when the create query has succeeded ensures the
  // form doesn't unlock in the brief moment between the mutation succeeding and the user
  // being sent to the thanks page
  const isLoading =
    createSubscriptionResult.isLoading ||
    createSubscriptionResult.isSuccess ||
    updateSubscriptionResult.isLoading;

  const isUpdateSubscription = Boolean(
    user && user.subscription && !hasCreationInitiated
  );

  const checkoutErrorMessage = useMemo(
    () => selectErrorMessageFromResult(createSubscriptionResult),
    [createSubscriptionResult]
  );

  useEffect(() => {
    if (isValidPlanType(query.planType)) {
      setSelectedPlanType(query.planType);
    }
  }, [isRouterReady]);

  return (
    <>
      <Head title="Your plan" />
      <Plan
        user={user}
        location={location}
        updateSubscription={updateSubscriptionMutation}
        isLoading={isLoading}
        isUpdateSubscription={isUpdateSubscription}
        selectedPlanType={selectedPlanType}
        onSelectedPlanTypeChange={setSelectedPlanType}
        selectedPlanId={selectedPlanId}
        onSelectedPlanIdChange={setSelectedPlanId}
        renderCheckoutForm={(props) => (
          <Plan.CheckoutForm
            {...props}
            errorMessage={checkoutErrorMessage}
            isLoading={isLoading}
            isProduction={isProductionEnv}
            location={location}
            onSubmit={onSubmitCheckoutForm}
            stripe={stripe}
            subscribeUser={createSubscriptionMutation}
          />
        )}
      />
    </>
  );
}

const isValidPlanType = (planType: any): planType is string => {
  return (
    typeof planType === "string" && Object.values(planTypes).includes(planType)
  );
};
